import React, {useState} from 'react'
import Action from 'src/models/Action'
import TextInput from '../../components/TextInput'
import Body1 from '../../components/Typography/Body1'
import {Grid, Avatar, Box} from '@material-ui/core'
import Autocomplete from 'src/components/Autocomplete'
import ActionType from 'src/models/ActionType'
import BodyArea from 'src/models/BodyArea'
import Equipment from 'src/models/Equipment'
import {DropzoneArea} from 'material-ui-dropzone'
import Styled from '../Styled'
import styles from './styles'
import {FormContainer, Root} from './styled'
import EditTemplate from '../EditTemplate'
import Subtitle2 from '../Typography/Subtitle2'

const Label = Body1
const Thumbnail = Styled(Avatar, styles.thumbnail)

type ActionFormProps = {
  action: Action
  title: string
  allBodyAreas: BodyArea[]
  allEquipment: Equipment[]
  allActionTypes: ActionType[]
  successString?: string
  busyString?: string
  errorMessage?: string
  onSavePress: (action: Action, videoFile?: File) => void
  onCancel: () => void
  onDismissSuccess: () => void
}

const ActionForm: React.FC<ActionFormProps> = ({
  action,
  title,
  allBodyAreas,
  allEquipment,
  allActionTypes,
  successString,
  busyString,
  errorMessage,
  onSavePress,
  onCancel,
  onDismissSuccess,
}) => {
  const [newAction, setNewAction] = useState(action)
  const [videoFile, setVideoFile] = useState<File | undefined>()

  ///
  /// Called when the save button is clicked.
  ///
  const onSaveClick = () => {
    // Need to save this routine.
    onSavePress(newAction, videoFile)
  }

  return (
    <Root>
      <EditTemplate
        title={title}
        busyString={busyString}
        errorMessage={errorMessage}
        successString={successString}
        allFieldsRequired
        onSave={onSaveClick}
        onCancel={onCancel}
        onDismissSuccess={onDismissSuccess}>
        <FormContainer border={1} borderColor="text.secondary" borderRadius={10}>
          <Grid container spacing={5} xs={12} alignItems="center" justify="center">
            <Grid item xs={12}>
              <TextInput
                fullWidth
                onChangeText={(actionTitle: string) =>
                  setNewAction({...newAction, title: actionTitle})
                }
                label="Title"
                value={newAction.title}
              />
            </Grid>
            {newAction.author && (
              <Grid item xs={12}>
                <Label>Author: {newAction.author.name}</Label>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextInput
                multiline
                fullWidth
                onChangeText={(description: string) => setNewAction({...newAction, description})}
                label="Description"
                value={newAction.description}
              />
            </Grid>
            <Grid item xs={12}>
              <TextInput
                fullWidth
                multiline
                onChangeText={(steps: string) => setNewAction({...newAction, steps})}
                label="Steps"
                value={newAction.steps}
              />
            </Grid>
            <Grid item container xs={12}>
              <Grid item xs={12}>
                <Label>Video</Label>
              </Grid>
              <Grid item xs={6}>
                <Thumbnail variant="square" src={newAction.thumbnailUrl} />
              </Grid>
              <Grid item xs={6}>
                <DropzoneArea
                  showPreviewsInDropzone={true}
                  showFileNames={true}
                  showPreviews={false}
                  acceptedFiles={['video/mp4']}
                  maxFileSize={1073741824} // one gigabyte
                  filesLimit={1}
                  onChange={(selected) => {
                    setVideoFile(selected.shift())
                  }}
                />
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <TextInput
                fullWidth
                onChangeText={(setsAndReps: string) => setNewAction({...newAction, setsAndReps})}
                label="Sets and reps"
                value={newAction.setsAndReps}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                id="actiontypes"
                title="Action Type"
                placeholderText="Select an action type"
                value={newAction.actionType}
                data={allActionTypes}
                onChange={(actionType) =>
                  setNewAction({...newAction, actionType: actionType as ActionType})
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Box>
                <Autocomplete
                  id="equipment"
                  title="Equipment Needed"
                  placeholderText="Select equipment"
                  value={newAction.equipment}
                  data={allEquipment}
                  multiple
                  onChange={(equipment) =>
                    setNewAction({...newAction, equipment: equipment as Equipment[]})
                  }
                />
                <Subtitle2>
                  Selecting no equipment indicates that no equipment is needed for this action.
                </Subtitle2>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                id="bodyareas"
                title="Targeted body area"
                placeholderText="Select body area"
                value={newAction.bodyAreas}
                data={allBodyAreas}
                multiple
                onChange={(bodyArea) =>
                  setNewAction({...newAction, bodyAreas: bodyArea as BodyArea[]})
                }
              />
            </Grid>
          </Grid>
        </FormContainer>
      </EditTemplate>
    </Root>
  )
}

export default ActionForm
